/**
 * Full emoji autocomplete index built from emoji-picker-react's complete dataset.
 * Replaces the limited 248-emoji emojiData.ts for autocomplete searches.
 *
 * Each emoji has `n` (names/keywords), `u` (unified codepoint).
 * We convert unified → native emoji character and index all names for search.
 */
import emojis from 'emoji-picker-react/src/data/emojis.json';

export interface EmojiAutocompleteEntry {
  shortcode: string;  // Primary name (first in names array)
  native: string;     // Native emoji character
  names: string[];    // All searchable names/keywords
}

/** Convert unified hex code (e.g. "1f600" or "1f469-200d-2764-fe0f-200d-1f48b-200d-1f468") to native emoji */
function unifiedToNative(unified: string): string {
  return unified
    .split('-')
    .map((hex) => String.fromCodePoint(parseInt(hex, 16)))
    .join('');
}

/** Build the full emoji index (lazy, cached) */
let _cachedIndex: EmojiAutocompleteEntry[] | null = null;
let _cachedMap: Map<string, EmojiAutocompleteEntry> | null = null;

function buildIndex(): { list: EmojiAutocompleteEntry[]; map: Map<string, EmojiAutocompleteEntry> } {
  if (_cachedIndex && _cachedMap) {
    return { list: _cachedIndex, map: _cachedMap };
  }

  const list: EmojiAutocompleteEntry[] = [];
  const map = new Map<string, EmojiAutocompleteEntry>();

  const groups = emojis as Record<string, Array<{ n: string[]; u: string; v?: string[] }>>;
  for (const group of Object.values(groups)) {
    if (!Array.isArray(group)) continue;
    for (const emoji of group) {
      if (!emoji.n || !emoji.u) continue;
      const native = unifiedToNative(emoji.u);
      // Use first name as the primary shortcode, replacing spaces with underscores
      const shortcode = emoji.n[0].replace(/\s+/g, '_').replace(/[,:]/g, '').toLowerCase();
      const names = emoji.n.map((n) => n.toLowerCase());
      const entry: EmojiAutocompleteEntry = { shortcode, native, names };
      list.push(entry);
      // Map by primary shortcode
      map.set(shortcode, entry);
      // Also map by alternate names
      for (let i = 1; i < names.length; i++) {
        const altKey = names[i].replace(/\s+/g, '_').replace(/[,:]/g, '');
        if (!map.has(altKey)) {
          map.set(altKey, entry);
        }
      }
    }
  }

  _cachedIndex = list;
  _cachedMap = map;
  return { list, map };
}

/** Everyday shortcodes ranked ahead of obscure prefix matches (matched against any emoji name). */
const POPULAR_SHORTCODES: ReadonlySet<string> = new Set([
  'joy', 'heart', 'thumbsup', '+1', 'smile', 'grin', 'laughing', 'wink', 'blush',
  'heart_eyes', 'fire', 'tada', 'clap', 'pray', 'ok_hand', 'rocket', 'eyes',
  'thinking_face', 'sob', 'sweat_smile', 'rofl', 'muscle', 'star', 'sparkles',
  'white_check_mark', 'heavy_check_mark', 'x', 'warning', 'coffee', 'sunny', '100',
  'raised_hands', 'wave', 'point_right', 'bulb', 'memo', 'calendar', 'camera',
  'sunglasses', 'hugs', 'star-struck', 'partying_face',
]);

const normalizeName = (name: string): string => name.replace(/\s+/g, '_').replace(/[,:]/g, '');

const isDeprioritized = (entry: EmojiAutocompleteEntry): boolean =>
  entry.shortcode.startsWith('flag-') || entry.names.some((n) => n.includes('skin tone'));

/**
 * Search emojis by query string. Matches against all names/keywords.
 * Ranking tiers (stable, shorter shortcode first within a tier):
 *   0 exact name match, 1 popular prefix, 2 other prefix,
 *   3 prefix on flags/skin-tone variants, 4 substring (flags/skin tones last).
 */
export function searchEmojis(
  query: string,
  limit = 8
): Array<{ shortcode: string; native: string }> {
  if (!query || query.length < 2) return [];

  const { list } = buildIndex();
  const q = query.toLowerCase();

  const matches: Array<{ shortcode: string; native: string; tier: number; index: number }> = [];

  list.forEach((entry, index) => {
    const normalized = entry.names.map(normalizeName);
    let tier = -1;
    if (normalized.includes(q)) {
      tier = 0;
    } else if (normalized.some((n) => n.startsWith(q))) {
      if (isDeprioritized(entry)) tier = 3;
      else if (normalized.some((n) => n.startsWith(q) && POPULAR_SHORTCODES.has(n))) tier = 1;
      else tier = 2;
    } else if (normalized.some((n) => n.includes(q))) {
      tier = isDeprioritized(entry) ? 5 : 4;
    }
    if (tier >= 0) matches.push({ shortcode: entry.shortcode, native: entry.native, tier, index });
  });

  matches.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.shortcode.length !== b.shortcode.length) return a.shortcode.length - b.shortcode.length;
    return a.index - b.index;
  });

  return matches.slice(0, limit).map(({ shortcode, native }) => ({ shortcode, native }));
}

/**
 * Get emoji by exact shortcode. Falls back to partial match.
 */
export function getEmojiByShortcode(shortcode: string): EmojiAutocompleteEntry | undefined {
  const { map } = buildIndex();
  const key = shortcode.toLowerCase().replace(/\s+/g, '_').replace(/[,:]/g, '');
  return map.get(key);
}
